/**
 * Hook für Offline-Submission Sync mit Auto-Retry
 * Koordiniert IndexedDB, SyncQueue und API-Calls
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnlineStatus } from './useOnlineStatus';
import { getSyncQueue, PendingSyncItem } from '../lib/syncQueue';
import { getOfflineDB } from '../lib/offlineDB';
import { api } from '../lib/api/client';
import { ApiError, isServerUnavailableError } from '../lib/api/http';
import { notifications } from '@mantine/notifications';

import { openSyncErrorDialog } from '../components/SyncErrorDialog';
import { useAuthStore } from '../stores/auth';

/**
 * Server-Fehlermeldungen sind teils Entwickler-Prosa (Prisma-Feldlisten,
 * Stacktraces) — für die Anzeige eine Erklärung in Alltagssprache ableiten,
 * die Rohfassung wandert in die "Technischen Details" des Dialogs.
 */
function humanizeSyncError(message: string | undefined): string {
  const m = message ?? '';
  if (/unknown argument|invalid value|prisma|argument `/i.test(m)) {
    return 'Der Entwurf enthält Daten, die der Server nicht verarbeiten konnte (vermutlich ein Versions-Unterschied zwischen App und Server — nach einem Browser-Reload meist behoben).';
  }
  if (/permission denied/i.test(m)) {
    return 'Keine Berechtigung: Dieser Entwurf gehört einem anderen Benutzer.';
  }
  if (/too large|FST_ERR_CTP_BODY_TOO_LARGE/i.test(m)) {
    return 'Der Bericht ist zu groß für den Server — vermutlich die Fotos. Bitte den Bericht öffnen, Fotos entfernen oder neu aufnehmen und erneut speichern.';
  }
  if (/conflict/i.test(m)) {
    return 'Der Bericht wurde zwischenzeitlich an anderer Stelle geändert.';
  }
  return m || 'Der Bericht konnte nicht zum Server übertragen werden.';
}

interface SyncResult {
  submissionId: string;
  success: boolean;
  conflicted?: boolean;
  /** Bericht ist inzwischen eingereicht — der Server nimmt den Zwischenstand nicht mehr an. */
  obsolete?: boolean;
  /** Server nicht erreichbar (Deploy, Netz) — später erneut, zählt nicht als Fehlversuch. */
  deferred?: boolean;
  remoteTimestamp?: number;
  syncedAt?: number;
  status?: string;
  message?: string;
  technicalDetail?: string;
}

interface SyncState {
  isSyncing: boolean;
  lastSyncTime?: number;
  pendingCount: number;
  errors: Record<string, string>;
  conflicts: Record<string, PendingSyncItem>;
}

const SYNC_DEBOUNCE_MS = 2000; // Debounce: Nicht sofort syngen wenn online kommt

/**
 * Nachsenden, solange etwas wartet. Bis v1.354.2 lief der Abgleich nur beim App-Start und beim
 * Wechsel offline → online: Nach einem Deploy blieb die Warteschlange liegen, bis jemand die App
 * neu öffnete.
 */
const RETRY_INTERVAL_MS = 30_000;

/**
 * Hook für Offline-Sync-Verwaltung
 * Automatisch bei Netzwerk-Reconnection
 */
export function useSync(): SyncState & {
  syncNow: () => Promise<void>;
  resolveConflict: (submissionId: string, resolvedData: Record<string, any>) => void;
  discardLocal: (submissionId: string) => void;
} {
  const { isOnline } = useOnlineStatus();
  const navigate = useNavigate();
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<number>();
  const [pendingCount, setPendingCount] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [conflicts, setConflicts] = useState<Record<string, PendingSyncItem>>({});

  const queue = getSyncQueue();
  const db = getOfflineDB();

  /**
   * Aktualisiere lokale State aus Queue
   */
  const updateStats = useCallback(() => {
    const pending = queue.getPending();
    setPendingCount(pending.length);

    const conflictMap = pending
      .filter((item) => item.conflicted)
      .reduce(
        (acc, item) => {
          acc[item.submissionId] = item;
          return acc;
        },
        {} as Record<string, PendingSyncItem>
      );
    setConflicts(conflictMap);
  }, [queue]);

  /**
   * Führe einen Sync-Versuch durch
   */
  const syncNow = useCallback(async () => {
    // Ansicht als Nutzer: nichts aus der eigenen Warteschlange abschicken — der Server sperrt
    // Schreibzugriffe (403), die Berichte würden sonst als fehlgeschlagen markiert.
    if (useAuthStore.getState().user?.viewAs) return;
    // Verhindere parallele Syncs
    if (isSyncing || !queue.acquireLock()) {
      console.log('[useSync] Sync already in progress or lock acquired by another');
      return;
    }

    setIsSyncing(true);
    const newErrors: Record<string, string> = {};

    try {
      const readyItems = queue.getReadyForSync();

      if (readyItems.length === 0) {
        console.log('[useSync] No items ready for sync');
        updateStats();
        return;
      }

      console.log(`[useSync] Starting sync of ${readyItems.length} items`);

      try {
        // Jeden Bericht in einem EIGENEN Request. Gebündelt riss ein einziger zu
        // großer Bericht (Fotos, 413 vor dem Handler) alle anderen mit: der ganze
        // Batch scheiterte, alle Einträge galten als fehlgeschlagen — endlos.
        const results: SyncResult[] = [];
        let syncedCount = 0;
        for (const item of readyItems) {
          const submission: any = {
            submissionId: item.submissionId,
            formType: item.formType,
            data: item.data,
            localTimestamp: item.localTimestamp,
          };
          // Only include remoteTimestamp if defined
          if (item.remoteTimestamp !== undefined) {
            submission.remoteTimestamp = item.remoteTimestamp;
          }
          try {
            const response = await api.syncSubmissions({ submissions: [submission] });
            results.push(...response.results);
            syncedCount += response.synced;
          } catch (itemError) {
            // Server startet neu (Deploy) oder Netz weg: Am Bericht ist nichts falsch. Später
            // erneut — ohne einen der drei Versuche zu verbrauchen und ohne rote Meldung.
            if (isServerUnavailableError(itemError)) {
              results.push({
                submissionId: item.submissionId,
                success: false,
                deferred: true,
                message: itemError instanceof Error ? itemError.message : 'Server nicht erreichbar',
              });
              continue;
            }
            // Request selbst abgelehnt (413, 500 …) — als Fehler DIESES Berichts
            // führen, nicht als Verbindungsabbruch für alle.
            const tooLarge =
              itemError instanceof ApiError &&
              (itemError.code === 'FST_ERR_CTP_BODY_TOO_LARGE' || /too large/i.test(itemError.summary));
            results.push({
              submissionId: item.submissionId,
              success: false,
              message: tooLarge
                ? 'Request body is too large'
                : itemError instanceof Error
                  ? itemError.message
                  : 'Sync failed',
            });
          }
        }
        const response = { results, synced: syncedCount };

        // Verarbeite Ergebnisse
        for (const result of response.results) {
          if (result.success) {
            queue.markSynced(result.submissionId);
            console.log(`[useSync] Synced: ${result.submissionId}`);
          } else if (result.obsolete) {
            // Inzwischen eingereicht: Dieser Zwischenstand kommt nie mehr an. Verwerfen, statt
            // bei jedem Abgleich wieder „konnte nicht synchronisiert werden" zu melden.
            queue.remove(result.submissionId);
            notifications.hide(`sync-failed-${result.submissionId}`);
            console.log(`[useSync] Verworfen, bereits eingereicht: ${result.submissionId}`);
          } else if (result.deferred) {
            queue.markFailed(result.submissionId, result.message, { countsAsAttempt: false });
            console.log(`[useSync] Server nicht erreichbar, später erneut: ${result.submissionId}`);
          } else if (result.conflicted) {
            // Hat die geöffnete Seite den Bericht inzwischen selbst gespeichert, ist der Eintrag
            // weg — dann gibt es nichts zu entscheiden.
            if (!queue.get(result.submissionId)) continue;
            queue.markConflicted(
              result.submissionId,
              result.remoteTimestamp || Date.now(),
              'Conflict: Submission was modified remotely'
            );
            console.log(`[useSync] Conflict detected: ${result.submissionId}`);

            // Benachrichtige Benutzer über Konflikt
            notifications.show({
              title: 'Synchronisierungs-Konflikt',
              message:
                'Ein Bericht wurde an anderer Stelle weiterbearbeitet, während Änderungen von diesem Gerät noch nicht übertragen waren. Bitte wählen, welcher Stand gilt.',
              color: 'yellow',
              autoClose: false,
            });
          } else {
            queue.markFailed(result.submissionId, result.message);
            newErrors[result.submissionId] = result.message || 'Sync failed';
            console.log(`[useSync] Failed: ${result.submissionId} - ${result.message}`);
          }
        }

        // Fehlgeschlagene Submissions melden: konkret (echte Ursache) und klickbar.
        // Stabile Notification-IDs sorgen dafür, dass Retry-Läufe die BESTEHENDE
        // Meldung aktualisieren statt sie zu stapeln. Ab mehr als 3 betroffenen
        // Berichten eine einzelne Sammelmeldung mit Anzahl — 30 Einzel-Toasts
        // würden den Bildschirm fluten (Mantine zeigt max. ~5, der Rest staut).
        const failedResults = response.results.filter(
          (r) => !r.success && !r.conflicted && !r.obsolete && !r.deferred
        );
        if (failedResults.length > 0 && failedResults.length <= 3) {
          notifications.hide('sync-failed-summary');
          for (const result of failedResults) {
            const failedItem = readyItems.find((i) => i.submissionId === result.submissionId);
            const humanMessage = humanizeSyncError(result.message);
            notifications.show({
              id: `sync-failed-${result.submissionId}`,
              title: 'Bericht konnte nicht synchronisiert werden',
              message: `${humanMessage} Klicken für Details.`,
              color: 'red',
              autoClose: false,
              style: { cursor: 'pointer' },
              onClick: () => {
                notifications.hide(`sync-failed-${result.submissionId}`);
                openSyncErrorDialog({
                  submissionId: result.submissionId,
                  formType: failedItem?.formType ?? 'ONSITE_SERVICE',
                  humanMessage,
                  technicalDetail: result.technicalDetail ?? result.message,
                });
              },
            });
          }
        } else if (failedResults.length > 3) {
          for (const result of failedResults) {
            notifications.hide(`sync-failed-${result.submissionId}`);
          }
          notifications.show({
            id: 'sync-failed-summary',
            title: `Synchronisierung fehlgeschlagen (${failedResults.length} Berichte)`,
            message: `${humanizeSyncError(failedResults[0]?.message)} (u. a.) — zum Öffnen der Berichte klicken.`,
            color: 'red',
            autoClose: false,
            style: { cursor: 'pointer' },
            onClick: () => {
              notifications.hide('sync-failed-summary');
              navigate('/service-report');
            },
          });
        }

        // Erfolgs-Benachrichtigung
        if (response.synced > 0) {
          notifications.show({
            title: 'Synchronisierung erfolgreich',
            message: `${response.synced} Submission(s) synchronisiert`,
            color: 'green',
            autoClose: 3000,
          });
        }

        setLastSyncTime(Date.now());
      } catch (apiError) {
        const message = apiError instanceof Error ? apiError.message : 'Sync failed';
        console.error('[useSync] API error:', apiError);

        // Markiere alle als fehlgeschlagen
        readyItems.forEach((item) => {
          queue.markFailed(item.submissionId, message);
        });

        notifications.show({
          title: 'Synchronisierungsfehler',
          message: `Verbindungsfehler: ${message}`,
          color: 'red',
          autoClose: 5000,
        });
      }
    } finally {
      queue.releaseLock();
      setIsSyncing(false);
      updateStats();
    }
  }, [isSyncing, queue, updateStats]);

  /**
   * Konflikt manuell auflösen
   */
  const resolveConflict = useCallback(
    (submissionId: string, resolvedData: Record<string, any>) => {
      queue.resolveConflict(submissionId, resolvedData);
      updateStats();
      console.log(`[useSync] Conflict resolved: ${submissionId}`);
    },
    [queue, updateStats]
  );

  /**
   * Konflikt zugunsten des Server-Stands auflösen: Die Änderungen von diesem Gerät verwerfen.
   */
  const discardLocal = useCallback(
    (submissionId: string) => {
      queue.remove(submissionId);
      updateStats();
      console.log(`[useSync] Lokaler Stand verworfen: ${submissionId}`);
    },
    [queue, updateStats]
  );

  /**
   * Auto-Sync wenn online kommt
   */
  useEffect(() => {
    if (!isOnline) {
      return; // Nicht online - kein Sync
    }

    // Debounce: Warte kurz bevor wir syngen
    const timeout = setTimeout(() => {
      const pending = queue.getPending();
      if (pending.length > 0) {
        console.log(`[useSync] Online detected, ${pending.length} pending submissions. Starting sync...`);
        void syncNow();
      }
    }, SYNC_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [isOnline, queue, syncNow]);

  // Immer die aktuelle syncNow-Instanz — das Intervall soll nicht bei jedem isSyncing neu starten.
  const syncNowRef = useRef(syncNow);
  syncNowRef.current = syncNow;

  /**
   * Regelmäßig nachsenden (RETRY_INTERVAL_MS)
   */
  useEffect(() => {
    if (!isOnline) return;
    const interval = setInterval(() => {
      if (queue.getReadyForSync().length > 0) void syncNowRef.current();
    }, RETRY_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isOnline, queue]);

  /**
   * Regelmäßig Stats aktualisieren
   */
  useEffect(() => {
    const interval = setInterval(() => {
      updateStats();
    }, 2000);

    return () => clearInterval(interval);
  }, [updateStats]);

  return {
    isSyncing,
    lastSyncTime,
    pendingCount,
    errors,
    conflicts,
    syncNow,
    resolveConflict,
    discardLocal,
  };
}

/**
 * Hook um zu checken ob User Konflikte hat die Resolution brauchen
 */
export function useConflicts(): PendingSyncItem[] {
  const [conflicts, setConflicts] = useState<PendingSyncItem[]>([]);
  const queue = getSyncQueue();

  useEffect(() => {
    const update = () => {
      const pending = queue.getPending();
      // Geöffnete Berichte fragen selbst (components/UnsyncedDraftModal) — nicht zweimal.
      setConflicts(pending.filter((item) => item.conflicted && !queue.isHeld(item.submissionId)));
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [queue]);

  return conflicts;
}
